// src/modules/bookLocation/keyHandover/hooks/useSubscriptionHydration.ts

import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch } from "../../../../redux/useDispatch";
import { parseSubscriptionQuery } from "../common/url/subscriptionQuery";
import {
    setHasExistingSubscription,
    setSelectedSubscriptionPlan,
    setNewSubscriptionHostData,
    setNewSubscriptionEmail,
    setNewSubscriptionEmailVerified,
    setNewSubscriptionEligibility,
    setNewSubscriptionPaymentCompleted,
    setNewSubscriptionId,
    setExistingSubscriptionEmail,
    setExistingSubscriptionVerified,
    setExistingSubscriptionCustomerAgreementId,
} from "../../../../redux/slice/subscription/subscriptionSlice";
import { setKeyPaymentModel } from "../../../../redux/slice/bookingCreation/bookingCreationSlice";

/**
 * Hydrate subscription flow state from URL query parameters
 *
 * Handles browser refresh and back/forward navigation for subscription flows.
 * Restores Redux state from URL on mount.
 *
 * @remarks
 * - Runs once per mount
 * - Handles both Flow 2 (new subscription) and Flow 3 (existing subscription)
 * - Used for browser refresh and deep linking support
 * - Returns true when hydration is complete
 *
 * @returns boolean indicating whether hydration is complete
 *
 * @example
 * ```tsx
 * function SubscriptionPage() {
 *   const subscriptionHydrated = useSubscriptionHydration();
 *   if (!subscriptionHydrated) return <Spinner />;
 *   // Subscription state now hydrated from URL
 * }
 * ```
 */
export function useSubscriptionHydration(): boolean {
    const location = useLocation();
    const dispatch = useAppDispatch();
    const hydratedRef = useRef(false);
    const capturedRef = useRef(false);
    const initialQueryRef = useRef<ReturnType<typeof parseSubscriptionQuery> | null>(null);
    const [isHydrated, setIsHydrated] = useState(false);

    // Capture initial URL parameters once on mount
    useEffect(() => {
        if (capturedRef.current) return;

        const query = parseSubscriptionQuery(location.search);
        initialQueryRef.current = query;
        capturedRef.current = true;
    }, [location.search]);

    // Hydrate Redux state from captured URL parameters
    useEffect(() => {
        // Only run once
        if (hydratedRef.current) return;

        // Wait for query capture to complete
        if (!capturedRef.current) {
            return;
        }

        const query = initialQueryRef.current;

        if (!query) {
            if (!hydratedRef.current) {
                hydratedRef.current = true;
                setIsHydrated(true);
            }
            return;
        }

        // Hydrate payment model
        if (query.paymentModel) {
            dispatch(setKeyPaymentModel(query.paymentModel));
        }

        // Hydrate subscription flow type
        if (query.subscriptionFlow) {
            dispatch(setHasExistingSubscription(query.subscriptionFlow));
        }

        // Hydrate Flow 2: New Subscription
        if (query.subscriptionFlow === "new") {
            if (query.email) {
                dispatch(setNewSubscriptionEmail(query.email));
            }
            if (query.emailVerified === "true") {
                dispatch(setNewSubscriptionEmailVerified(true));
            }
            if (query.eligible !== undefined) {
                dispatch(
                    setNewSubscriptionEligibility({
                        isEligible: query.eligible === "true",
                    })
                );
            }
            if (query.hostFirstName || query.hostLastName || query.hostEmail) {
                dispatch(
                    setNewSubscriptionHostData({
                        firstName: query.hostFirstName || null,
                        lastName: query.hostLastName || null,
                        email: query.hostEmail || null,
                        phone: query.hostPhone || null,
                    })
                );
            }
            if (query.subscriptionPaid === "true") {
                dispatch(setNewSubscriptionPaymentCompleted(true));
            }
            if (query.subscriptionId) {
                dispatch(setNewSubscriptionId(query.subscriptionId));
            }
        }

        // Hydrate Flow 3: Existing Subscription
        if (query.subscriptionFlow === "existing") {
            if (query.email) {
                dispatch(setExistingSubscriptionEmail(query.email));
            }
            if (query.subscriptionVerified === "true") {
                dispatch(setExistingSubscriptionVerified(true));
            }
            // Check for customerAgreementId in URL (used by existing flow)
            const urlParams = new URLSearchParams(location.search);
            const customerAgreementId = urlParams.get('customerAgreementId') || query.subscriptionId;
            if (customerAgreementId) {
                dispatch(setExistingSubscriptionCustomerAgreementId(customerAgreementId));
            }
        }

        if (!hydratedRef.current) {
            hydratedRef.current = true;
            setIsHydrated(true);
        }
    }, [dispatch]);

    return isHydrated;
}
